import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AnalyticsSummaryResponse, HeatmapResponse } from '@/api/types';
import { buildOuTree, type OuNode } from '@/components/common/OuTree/ouTree';
import { useOuScope } from '@/hooks/useOuScope';
import { scoreStyle, fmtScore, getInitials } from '@/utils/analytics';
import { DimensionFilterNotice } from '../components/DimensionFilterNotice';
import {
  SIN_OU_KEY,
  aggregateDirectlyAssigned,
  aggregateHeatmapByOu,
  averageForFundamental,
  mergeAggregateForNode,
  type OuScoreAggregate,
} from '@/utils/heatmapAggregation';

interface HeatmapTabProps {
  apiHeatmap?: HeatmapResponse;
  apiSummary?: AnalyticsSummaryResponse;
  /** Dimensión seleccionada en el filtro; `null` cuando el reporte cubre todas. */
  activeDimensionName?: string | null;
}

/** Clave sintética de la fila con las personas asignadas a la OU abierta y no a una hija. */
const DIRECT_ROW_KEY = '__asignados-directos__';

/** Fila de la tabla en el nivel de drill-down actual. */
interface HeatmapRow {
  id: string;
  name: string;
  peopleCount: number;
  /** true si la fila tiene sub-OUs reales y por lo tanto se puede navegar hacia abajo. */
  isDrillable: boolean;
  /** true si la fila agrupa a las personas asignadas directamente a la OU abierta. */
  isDirectAssignment: boolean;
}

const SCORE_LEGEND = [
  { range: '≤ 2.0', bg: '#fef2f2', ring: '#fca5a5', labelKey: 'analytics.heatmap.legendCritical' },
  { range: '2.0–2.5', bg: '#fffbeb', ring: '#fcd34d', labelKey: 'analytics.heatmap.legendLow' },
  { range: '2.5–3.0', bg: '#fefce8', ring: '#fde047', labelKey: 'analytics.heatmap.legendMedium' },
  { range: '3.0–3.5', bg: '#ecfdf5', ring: '#6ee7b7', labelKey: 'analytics.heatmap.legendGood' },
  { range: '≥ 3.5',  bg: '#d1fae5', ring: '#34d399', labelKey: 'analytics.heatmap.legendExcellent' },
];

const KPI_COLORS = ['#059669', '#F08521', '#2563eb', '#D97706'];

/** Claves que rotulan la misma columna en el mini KPI, el encabezado y el `data-label`. */
const AVG_SCORE_KEY = 'analytics.kpi.avgScore';
const EVALUATIONS_KEY = 'analytics.kpi.evaluations';

export function HeatmapTab({
  apiHeatmap,
  apiSummary,
  activeDimensionName = null,
}: HeatmapTabProps) {
  const { t } = useTranslation();
  // Catálogo recortado al alcance del usuario: el árbol de drill-down nunca muestra ramas
  // fuera de las OUs que administra.
  const { scopedOuItems: ouItems } = useOuScope();
  const [isHidingEmpty, setIsHidingEmpty] = useState(true);

  const ouForest = useMemo<OuNode[]>(() => buildOuTree(ouItems), [ouItems]);

  // Ruta de breadcrumb: nodo actualmente "abierto" en cada nivel. Vacío = raíz (toda la org).
  const [drillPath, setDrillPath] = useState<OuNode[]>([]);
  const openNode = drillPath[drillPath.length - 1] ?? null;

  const currentLevelNodes = useMemo<OuNode[]>(
    () => (openNode === null ? ouForest : openNode.children),
    [openNode, ouForest],
  );

  // Memorizada aparte: sin esto la lista vacía por defecto sería una referencia nueva en cada
  // render y volvería a disparar todos los useMemo que dependen de las columnas.
  const columns = useMemo(() => apiHeatmap?.columns ?? [], [apiHeatmap]);
  const ouAggregates = useMemo(() => aggregateHeatmapByOu(apiHeatmap), [apiHeatmap]);

  // Agregados del nivel actual. Cada fila de OU suma TODOS sus descendientes reales; además, al
  // bajar un nivel se agrega la fila de personas asignadas a la OU abierta, que de otro modo
  // desaparecerían de la vista porque la tabla solo lista OUs hijas.
  const levelAggregates = useMemo(() => {
    const dimensionCount = columns.length;
    const aggregates = new Map<string, OuScoreAggregate>();

    for (const node of currentLevelNodes) {
      aggregates.set(node.ou.id, mergeAggregateForNode(node, ouAggregates, dimensionCount));
    }

    if (openNode !== null) {
      const direct = aggregateDirectlyAssigned(openNode, ouAggregates, dimensionCount);
      if (direct.participantCount > 0) aggregates.set(DIRECT_ROW_KEY, direct);
    } else if (ouAggregates.has(SIN_OU_KEY)) {
      aggregates.set(SIN_OU_KEY, ouAggregates.get(SIN_OU_KEY)!);
    }

    return aggregates;
  }, [currentLevelNodes, ouAggregates, columns.length, openNode]);

  // Matriz fila × dimensión ya promediada, a partir de `levelAggregates`.
  const cellLookup = useMemo(() => {
    const lookup = new Map<string, Map<string, number>>();
    for (const [rowId, aggregate] of levelAggregates) {
      const byDimension = new Map<string, number>();
      columns.forEach((column, index) => {
        const average = averageForFundamental(aggregate, index);
        if (average !== null) byDimension.set(column.id, average);
      });
      lookup.set(rowId, byDimension);
    }
    return lookup;
  }, [columns, levelAggregates]);

  const rows = useMemo<HeatmapRow[]>(() => {
    const levelRows: HeatmapRow[] = currentLevelNodes.map((node) => ({
      id: node.ou.id,
      name: node.ou.name,
      peopleCount: levelAggregates.get(node.ou.id)?.participantCount ?? 0,
      isDrillable: node.children.length > 0,
      isDirectAssignment: false,
    }));

    if (openNode !== null && levelAggregates.has(DIRECT_ROW_KEY)) {
      levelRows.unshift({
        id: DIRECT_ROW_KEY,
        name: t('analytics.heatmap.directlyAssigned', { ou: openNode.ou.name }),
        peopleCount: levelAggregates.get(DIRECT_ROW_KEY)!.participantCount,
        isDrillable: false,
        isDirectAssignment: true,
      });
    } else if (openNode === null && levelAggregates.has(SIN_OU_KEY)) {
      levelRows.push({
        id: SIN_OU_KEY,
        name: t('analytics.heatmap.noOu'),
        peopleCount: levelAggregates.get(SIN_OU_KEY)!.participantCount,
        isDrillable: false,
        isDirectAssignment: false,
      });
    }

    return levelRows;
  }, [currentLevelNodes, levelAggregates, openNode, t]);

  const emptyRowCount = rows.filter((row) => row.peopleCount === 0).length;
  const visibleRows = isHidingEmpty ? rows.filter((row) => row.peopleCount > 0) : rows;
  const hasDrillableRow = visibleRows.some((row) => row.isDrillable);

  function handleRowClick(row: HeatmapRow): void {
    if (!row.isDrillable) return;
    const node = currentLevelNodes.find((candidate) => candidate.ou.id === row.id);
    if (node) setDrillPath((current) => [...current, node]);
  }

  function scoreOf(row: HeatmapRow, dimensionId: string): number | null {
    return cellLookup.get(row.id)?.get(dimensionId) ?? null;
  }

  function rowAverage(row: HeatmapRow): number | null {
    const byDimension = cellLookup.get(row.id);
    if (!byDimension || byDimension.size === 0) return null;
    const scores = [...byDimension.values()];
    return scores.reduce((sum, score) => sum + score, 0) / scores.length;
  }

  // Mini KPIs — desde apiSummary, la misma fuente que consume ResumenTab, para que las dos
  // pestañas muestren exactamente el mismo número de evaluaciones y el mismo promedio.
  const miniKpis = [
    { label: t(AVG_SCORE_KEY), value: apiSummary ? fmtScore(apiSummary.overallAverage) : '—', color: KPI_COLORS[0] },
    { label: t(EVALUATIONS_KEY), value: (apiSummary?.totalEvaluations ?? 0).toLocaleString(), color: KPI_COLORS[1] },
    { label: t('analytics.kpi.evaluators'), value: (apiSummary?.uniqueEvaluators ?? 0).toLocaleString(), color: KPI_COLORS[2] },
    { label: t('analytics.kpi.responseRate'), value: `${apiSummary?.responseRate ?? 0}%`, color: KPI_COLORS[3] },
  ];

  return (
    <div className="au-card">
      <div className="au-card-header">
        <div>
          <div className="au-card-title">
            <i className="fas fa-th" aria-hidden="true" />
            {t('analytics.heatmap.title')}
          </div>
          <div className="au-card-sub">{t('analytics.heatmap.behaviorSubtitle')}</div>
        </div>
      </div>

      <DimensionFilterNotice dimensionName={activeDimensionName} />

      <div className="hm-mini-kpis">
        {miniKpis.map((kpi) => (
          <div key={kpi.label} className="hm-mini-kpi">
            <div className="hm-mini-kpi-label">{kpi.label}</div>
            <div className="hm-mini-kpi-val" style={{ color: kpi.color }}>{kpi.value}</div>
          </div>
        ))}
      </div>

      <div className="au-breadcrumb">
        <button
          type="button"
          className={`au-bc-btn${drillPath.length === 0 ? ' active' : ''}`}
          onClick={() => setDrillPath([])}
        >
          {t('analytics.heatmap.allOrg')}
        </button>
        {drillPath.map((node, index) => (
          <button
            type="button"
            key={node.ou.id}
            className={`au-bc-btn${index === drillPath.length - 1 ? ' active' : ''}`}
            onClick={() => setDrillPath((current) => current.slice(0, index + 1))}
          >
            {node.ou.name}
          </button>
        ))}
      </div>

      <div className="hm-toolbar">
        <div className="hm-legend-row">
          {SCORE_LEGEND.map((level) => (
            <span key={level.range} className="hm-legend-chip">
              <span className="hm-legend-dot" style={{ background: level.bg, borderColor: level.ring }} />
              {level.range} {t(level.labelKey)}
            </span>
          ))}
        </div>
        {emptyRowCount > 0 && (
          <label className="hm-toggle-empty">
            <input
              type="checkbox"
              checked={isHidingEmpty}
              onChange={(event) => setIsHidingEmpty(event.target.checked)}
            />
            {t('analytics.heatmap.hideEmpty', { count: emptyRowCount })}
          </label>
        )}
      </div>

      <div className="hm-wrap">
        <table className="hm-table">
          <thead>
            <tr>
              <th className="hm-th-name" scope="col">{t('analytics.heatmap.unitOU')}</th>
              {columns.map((column) => (
                <th key={column.id} className="hm-th-fund" scope="col">
                  <span className="hm-th-name-text" style={{ color: column.color ?? undefined }}>
                    {column.name}
                  </span>
                  {column.isBehavior && (
                    <span className="hm-th-parent">{column.fundamentalName}</span>
                  )}
                </th>
              ))}
              <th className="hm-th-fund" scope="col">{t(AVG_SCORE_KEY)}</th>
              <th className="hm-th-fund" scope="col">{t(EVALUATIONS_KEY)}</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.length === 0 ? (
              <tr>
                <td colSpan={columns.length + 3} className="hm-empty">
                  {t('analytics.noData')}
                </td>
              </tr>
            ) : visibleRows.map((row) => {
              const average = rowAverage(row);
              const averageStyle = average !== null ? scoreStyle(average) : null;
              const evaluations = levelAggregates.get(row.id)?.evaluationCount ?? 0;

              return (
                <tr
                  key={row.id}
                  className={[
                    'hm-row',
                    row.isDrillable ? 'drillable' : '',
                    row.isDirectAssignment ? 'hm-row-direct' : '',
                  ].filter(Boolean).join(' ')}
                  onClick={() => handleRowClick(row)}
                >
                  <td className="hm-td-name">
                    <div className="hm-name-cell">
                      <div className="hm-icon">{getInitials(row.name)}</div>
                      <div className="hm-name-body">
                        <div className="hm-name-row">
                          <span className="hm-name">{row.name}</span>
                          {row.isDrillable && (
                            <i className="fas fa-chevron-right hm-drill-arrow" aria-hidden="true" />
                          )}
                        </div>
                        <span className="hm-name-sub">
                          {t('analytics.heatmap.personCount', { count: row.peopleCount })}
                        </span>
                      </div>
                    </div>
                  </td>

                  {columns.map((column) => {
                    const score = scoreOf(row, column.id);
                    const cellStyle = score !== null ? scoreStyle(score) : null;
                    return (
                      <td key={column.id} className="hm-td-score" data-label={column.name}>
                        {cellStyle !== null ? (
                          <span
                            className="hm-score-chip"
                            style={{ background: cellStyle.bg, color: cellStyle.text, borderColor: cellStyle.ring }}
                          >
                            {fmtScore(score!)}
                          </span>
                        ) : (
                          <span className="hm-no-data">—</span>
                        )}
                      </td>
                    );
                  })}

                  <td className="hm-td-score" data-label={t(AVG_SCORE_KEY)}>
                    {averageStyle !== null ? (
                      <span
                        className="hm-score-chip hm-score-chip-total"
                        style={{ background: averageStyle.bg, color: averageStyle.text, borderColor: averageStyle.ring }}
                      >
                        {fmtScore(average!)}
                      </span>
                    ) : (
                      <span className="hm-no-data">—</span>
                    )}
                  </td>

                  <td className="hm-td-score hm-td-evals" data-label={t(EVALUATIONS_KEY)}>
                    {evaluations > 0 ? evaluations.toLocaleString() : <span className="hm-no-data">—</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {hasDrillableRow && <div className="hm-hint">{t('analytics.heatmap.hint')}</div>}
    </div>
  );
}
